import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/setup-status").then((r) => { if (r.data.needs_setup) nav("/setup", { replace: true }); }).catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/admin" : "/musteri", { replace: true });
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || "Giriş başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-[#0a192f] p-12 text-white lg:flex">
        <Brand light />
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">
            Çevre belgeleriniz<br />tek portalda.
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Firmalarınıza yayımlanan PDF belgeleri düzenli klasörlerde takip edin, kritik son tarihleri
            kaçırmayın. Müşterileriniz yalnızca kendi belgelerini güvenle görüntülesin.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-slate-400">
            <div><div className="font-heading text-2xl font-bold text-emerald-400">32</div>Belge Klasörü</div>
            <div><div className="font-heading text-2xl font-bold text-emerald-400">11</div>Süre Takip Türü</div>
            <div><div className="font-heading text-2xl font-bold text-emerald-400">KVKK</div>Firma İzolasyonu</div>
          </div>
        </div>
        <p className="text-xs text-slate-500">© 2026 Çevre Danışmanlık Belge Portalı</p>
      </div>

      <div className="flex items-center justify-center bg-[#f8fafc] p-6">
        <form onSubmit={submit} className="fade-in w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden"><Brand /></div>
          <h2 className="font-heading text-2xl font-bold text-[#0a192f]">Giriş Yap</h2>
          <p className="mt-1 text-sm text-slate-500">Hesabınızla portala erişin</p>

          {err && (
            <div data-testid="login-error" className="mt-5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {err}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" placeholder="ornek@firma.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Şifre</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" placeholder="••••••••" className="mt-1.5" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit-button"
            className="mt-6 w-full bg-emerald-600 hover:bg-emerald-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="mr-2 h-4 w-4" /> Giriş Yap</>}
          </Button>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-emerald-700 hover:underline">
              Şifremi unuttum
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
